import React from 'react';
import { Trans, useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import { Link, PrimaryButton, TertiaryIconButton, InfoInpage } from '@hz-design-system/web-ui';

import { trackGAEvent } from '../../../utils/gaTracking';
import Classes from './CarsContinueSearch.module.scss';

type TCarsContinueSearchProps = {
  searchEntry: TSearchEntry;
  asCard?: boolean;
  onRemoveLastSearch: Function;
};

const CarsContinueSearch = ({
  searchEntry: {
    link,
    l2CategoryNames,
    priceRangeFilter,
    constructionYearRangeFilter,
    mileageRangeFilter,
    attributeLabels,
  },
  asCard,
  onRemoveLastSearch,
}: TCarsContinueSearchProps) => {
  const { t } = useI18nContext();
  const priceRange = priceRangeFilter ? `${t('cars.search.fields.rangeTo')} ${priceRangeFilter}` : '';
  const mileage = mileageRangeFilter ? `${t('cars.search.fields.rangeTo')} ${mileageRangeFilter}` : '';
  const constructionYear = constructionYearRangeFilter
    ? `${t('cars.search.fields.rangeFromUpward')} ${constructionYearRangeFilter}`
    : '';
  let modelFilter;
  const otherFilters = [];
  Object.values(attributeLabels).forEach((attrLabel) => {
    if (attrLabel?.facetId === 'model') {
      modelFilter = attrLabel.label;
    } else {
      // @ts-expect-error: Backwards compatibility for different attrLabel types
      otherFilters.push(attrLabel?.label || attrLabel); // backwards compatible
    }
  });
  const l2Categories = l2CategoryNames?.length ? l2CategoryNames.join(', ') : '';
  const searchParts = [l2Categories, modelFilter, priceRange, mileage, constructionYear].concat(otherFilters);
  const searchString = searchParts.filter(Boolean).join(' · ');

  if (!searchString) {
    return;
  }

  const searchHandler = (event, lrpLink) => {
    event.preventDefault();
    trackGAEvent('Clicked', 'continue_search');
    window.location.assign(lrpLink);
  };

  return (
    <div className={asCard ? `PageContent-card ${Classes.asCard}` : Classes.inline}>
      {asCard ? (
        <>
          <Trans
            className={`${Classes.title} ${asCard ? 'hz-Title hz-Title--title5' : 'hz-Text hz-Text--bodySmallStrong'}`}
            i18nKey={'continue_search.title'}
          />
          <div title={searchString} className={Classes.searchParams}>
            {searchString}
          </div>
          <PrimaryButton
            className={Classes.button}
            onClick={(e) => {
              searchHandler(e, link);
            }}
          >
            <span>{t('continue_search.button')}</span>
          </PrimaryButton>
          <TertiaryIconButton
            name="close"
            className={Classes.removeButton}
            ariaLabel={t('continue_search.remove')}
            onClick={(e) => {
              onRemoveLastSearch(e);
            }}
          />
        </>
      ) : (
        <InfoInpage icon="info" title={t('continue_search.title')} utilities={['marginBottomS']}>
          <div className={Classes.container}>
            <Link
              title={searchString}
              className={Classes.searchParams}
              onClick={(e) => {
                searchHandler(e, link);
              }}
            >
              {searchString}
            </Link>
          </div>
        </InfoInpage>
      )}
    </div>
  );
};

export default CarsContinueSearch;
